import { yupResolver } from '@hookform/resolvers/yup';
import { useRouter } from 'next/router';
import React, { useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { loggerService } from '../../application/logger';
import { userService } from '../../application/user';
import Alert from '../../components/common/alert';
import Button from '../../components/common/button';
import LoadingSpinner from '../../components/common/loadingSpinner';
import FormContainer from '../../components/forms/formContainer';
import Input from '../../components/forms/input';
import Layout from '../../components/layout/layout';
import useUserWithRedirects from '../../hooks/useUser';

interface ForgotPasswordProps {}

interface Values {
  email: string;
}

const schema = Yup.object({
  email: Yup.string().email().required('required'),
});

const ForgotPassword: React.FC<ForgotPasswordProps> = () => {
  const apiKey = process.env.NEXT_PUBLIC_RECAPTCHA_KEY || '';
  const [recaptcha, setRecaptcha] = useState<string>('');
  const [status, setStatus] = useState<'success' | 'error' | null>(null);
  const router = useRouter();
  const user = useUserWithRedirects();

  const { register, handleSubmit, errors, formState } = useForm<Values>({
    resolver: yupResolver(schema),
    mode: 'onTouched',
  });

  const onCaptureChange = async (value: any) => {
    setRecaptcha(value);
  };

  const onSubmit = handleSubmit(async (values: Values) => {
    const applicationUser = userService();
    const applicationLogger = loggerService();
    setStatus(null);

    try {
      await applicationUser.userForgotPassword(values.email, recaptcha);
      setStatus('success');
    } catch (err) {
      const errCast = err as Error;
      applicationLogger.logErrorMessage({
        caller: 'forgotPassword.handleSubmit',
        message: `Failed when calling api forgot password: ${errCast.message}`,
      });
      setStatus('error');
    }
  });

  return (
    <Layout title="Forgot password" classes="bg-background-lighter">
      <FormContainer title="Forgot password">
        <form onSubmit={onSubmit} className="space-y-8">
          <Input
            label="Email"
            ref={register}
            name="email"
            errors={errors}
            defaultValue={router.query.email || user.email}
          />

          {!apiKey ? (
            <LoadingSpinner />
          ) : (
            <div data-cy="recaptcha-container">
              <ReCAPTCHA sitekey={apiKey} onChange={onCaptureChange} />
            </div>
          )}

          {status && (
            <Alert status={status}>
              {status === 'success'
                ? 'Please check your email for further instructions'
                : 'Account not found. Please try again.'}
            </Alert>
          )}

          <Button buttonStyle="primary" type="submit" classes="w-full">
            {formState.isSubmitting ? <LoadingSpinner /> : 'Submit'}
          </Button>
        </form>
      </FormContainer>
    </Layout>
  );
};

export default ForgotPassword;
